/** @jsx jsx */
import React, { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import InView from 'react-intersection-observer';
import { jsx } from '@emotion/core';
import {
  Grid,
  Typography,
  Link as MuiLink,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import PersonPinIcon from '@material-ui/icons/PersonPin';
import { Link as RouterLink } from 'react-router-dom';

import { SortToggle, ChallengeSection } from 'components';
import { challengeResults } from 'actions/challenge.actions';
import {
  ChallengePhaseType,
  ChallengeResultOrderType,
  ChallengeTypeType,
  ICustomTheme,
} from 'interfaces';
import {
  challengeResultsSelector,
  ChallengeResultsDataType,
  ChallengeResultsStateType,
  challengeSelector,
} from 'reducers/challenge.reducer';
import { userInfosSelector } from 'reducers/app.reducer';
import { CHALLENGE, RESULT_STATUS } from 'constants/challenges';
import { LOADING_STATUS } from 'constants/status';
import * as S from './styles';

const { cssArrowDown, cssPosition } = S;

const getSkeletonList = (length: number) =>
  Array.from(Array(length), (_, index) => index + 1);

const getUserResult = (
  data: ChallengeResultsDataType,
  userIdOrGroups: string | { id: number; name?: string }[] | undefined,
) =>
  data?.byId && userIdOrGroups
    ? Object.values(data.byId).find((result) =>
        typeof userIdOrGroups === 'string'
          ? result.targetId === userIdOrGroups
          : userIdOrGroups.some(
              (group) =>
                group.id ===
                Number(result.entity['@id']?.split?.('/')?.pop?.()),
            ),
      )
    : undefined;

const isValid = (
  needValidation: boolean,
  data: ChallengeResultsDataType,
  resultId: number,
) =>
  needValidation
    ? data?.byId?.[resultId].status === RESULT_STATUS.VALIDATED
    : true;

type Props = {
  title?: string;
  challengeId: number;
  challengeType: ChallengeTypeType;
  currentPhase?: ChallengePhaseType | null;
  isVisibleWhenNoData?: boolean;
  ItemsContainer?: React.ElementType;
  ItemSkeleton?: React.ElementType;
  ItemComponent?: React.ElementType | string;
  getItemProps?: (props: {
    data: ChallengeResultsDataType;
    resultId: number;
    index: number;
  }) => { [key: string]: unknown };
  skeletonLength?: number;
  needValidation?: boolean;
  children?: (results: ChallengeResultsStateType) => JSX.Element;
  order: ChallengeResultOrderType;
  setOrder: Dispatch<SetStateAction<ChallengeResultOrderType>>;
};

const ChallengeRanking = ({
  title = '',
  challengeId,
  challengeType,
  currentPhase,
  isVisibleWhenNoData = false,
  ItemsContainer = React.Fragment,
  ItemSkeleton = React.Fragment,
  ItemComponent = 'div',
  getItemProps = (props) => props,
  skeletonLength = 3,
  needValidation = false,
  children = undefined,
  order = 'asc',
  setOrder,
}: Props): JSX.Element | null => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { data: challengeData } = useSelector(challengeSelector(challengeId));
  const allResults = useSelector(challengeResultsSelector(challengeId));
  const { data, page, status, isFirstCall, isWaitingModeration, totalItems } =
    allResults;

  const getChallengeResultsByUser = () => {
    dispatch(challengeResults.request({ challengeId, order, challengeType }));
  };

  const stringData = JSON.stringify(data);
  const visibleResultsIds = useMemo(
    () =>
      data?.allIds.filter((id) =>
        challengeType === CHALLENGE.PHOTO
          ? data.allIds
          : data.byId[id].resultValue != null,
      ) ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stringData],
  );

  const { id: userId, userGroups } = useSelector(userInfosSelector);

  const theme: ICustomTheme = useTheme();
  const isDownXs = useMediaQuery(theme.breakpoints.down('xs'));

  const userResult = getUserResult(
    data,
    challengeData.isGroup ? userGroups : userId,
  );
  const isUserResultFetched = !!userResult;
  const isUserResultVisible = userResult?.resultValue != null;
  const hasResults = !!visibleResultsIds.length;

  useEffect(() => {
    if (status !== LOADING_STATUS.PENDING) {
      getChallengeResultsByUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      status !== LOADING_STATUS.PENDING &&
      totalItems &&
      data?.allIds.length < totalItems
    ) {
      // as long as user result is not fetched or when there is not visible result,
      // call next page to API
      if (!isUserResultFetched || !visibleResultsIds.length) {
        dispatch(
          challengeResults.request({
            challengeId,
            page: page + 1,
            order,
            challengeType,
          }),
        );
      }
    }
  }, [
    status,
    data,
    totalItems,
    page,
    isUserResultFetched,
    visibleResultsIds.length,
    challengeId,
    order,
    dispatch,
    challengeType,
  ]);

  const toggleOrder = () => {
    if (status === LOADING_STATUS.PENDING) {
      return;
    }
    const nextOrder = order === 'asc' ? 'desc' : 'asc';
    setOrder(nextOrder);
    dispatch(
      challengeResults.request({
        challengeId,
        order: nextOrder,
        challengeType,
      }),
    );
  };

  const handleGetNextResults = (inView: boolean) => {
    if (inView && status !== LOADING_STATUS.PENDING) {
      dispatch(
        challengeResults.request({
          challengeId,
          page: page + 1,
          order,
          challengeType,
        }),
      );
    }
  };

  const skeletonList = getSkeletonList(skeletonLength);

  const titleElement = <strong>{title}</strong>;

  if (
    !status ||
    (isFirstCall &&
      (status === LOADING_STATUS.IDLE || status === LOADING_STATUS.PENDING))
  ) {
    return (
      <ChallengeSection title={titleElement} defaultExpanded key="loading">
        {skeletonList.map((key) => (
          <ItemSkeleton key={key} />
        ))}
      </ChallengeSection>
    );
  }

  if (!isVisibleWhenNoData && !hasResults) {
    return (
      <ChallengeSection title={titleElement} key="withoutResult">
        <Typography>{t('challenges.no_result')}</Typography>
      </ChallengeSection>
    );
  }

  return (
    <React.Fragment>
      <ChallengeSection
        title={titleElement}
        defaultExpanded={hasResults}
        key="withResult"
      >
        <Grid container>
          {hasResults && (
            <Grid item xs>
              <SortToggle
                isAsc={order === 'asc'}
                toggleIsAsc={toggleOrder}
                ascLabel={t('challenges.detail.rank.ascending_rank')}
                descLabel={t('challenges.detail.rank.descending_rank')}
              />
            </Grid>
          )}
          {isUserResultVisible && (
            <Grid item css={{ textAlign: 'right' }} xs>
              <RouterLink to="#me" component={MuiLink} css={cssPosition}>
                {isDownXs ? (
                  <PersonPinIcon fontSize="large" />
                ) : (
                  <div css={cssPosition}>
                    <ArrowDownwardIcon css={cssArrowDown} />{' '}
                    {t('challenges.detail.rank.see_me')}{' '}
                    <ArrowDownwardIcon css={cssArrowDown} />
                  </div>
                )}
              </RouterLink>
            </Grid>
          )}

          <Grid item xs={12} css={{ marginTop: theme.spacing(4) }}>
            <Grid container spacing={6} justify="center">
              {!status ||
              (isFirstCall &&
                (status === LOADING_STATUS.IDLE ||
                  status === LOADING_STATUS.PENDING)) ? (
                skeletonList.map((key) => <ItemSkeleton key={key} />)
              ) : (
                <React.Fragment>
                  <ItemsContainer
                    currentPhase={currentPhase}
                    isWaitingModeration={isWaitingModeration}
                  >
                    {data?.allIds?.map((resultId, index) => {
                      if (
                        !visibleResultsIds.includes(resultId) ||
                        !isValid(needValidation, data, resultId)
                      ) {
                        return null;
                      }
                      const itemProps = {
                        ...getItemProps({ data, resultId, index }),
                        getChallengeResultsByUser,
                      };
                      if (
                        totalItems &&
                        index === data?.allIds.length - 1 &&
                        data?.allIds.length < totalItems
                      ) {
                        return (
                          <Grid item xs={12} md={6} lg={4} key={resultId}>
                            <InView onChange={handleGetNextResults}>
                              <ItemComponent {...itemProps} />
                            </InView>
                          </Grid>
                        );
                      }
                      return (
                        <Grid item xs={12} md={6} lg={4} key={resultId}>
                          <ItemComponent {...itemProps} />
                        </Grid>
                      );
                    })}
                  </ItemsContainer>
                  {status === LOADING_STATUS.PENDING &&
                    skeletonList.map((key) => <ItemSkeleton key={key} />)}
                </React.Fragment>
              )}
            </Grid>
          </Grid>
        </Grid>
      </ChallengeSection>
      {children?.(allResults)}
    </React.Fragment>
  );
};

export default ChallengeRanking;
